<template>
  <div :class="{ 'pl-1 pr-2 -mb-1': size === 'thumbnail' && !chartData }">
    <template v-if="chartData">
      <canvas :class="size === 'small' ? 'max-h-40 w-full' : 'chart-container'" ref="canvas" />
      <div
        v-if="$slots.caption"
        class="text-right text-12 text-gray-500 py-2"
        :class="{ 'chromatic-ignore': isChromatic }"
      >
        <slot name="caption" />
      </div>
    </template>
    <slot v-else name="placeholder">
      <img
        src="./chart-placeholder.svg"
        alt="loading"
        class="m-auto"
        :class="size === 'small' ? 'h-52 w-full' : 'aspect-[2/1]'"
        svg-inline
      />
    </slot>
  </div>
</template>

<script lang="ts" setup>
import {
  type ChartData,
  type ChartOptions,
  Chart,
  type ChartType,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Filler,
} from 'chart.js'

Chart.register(
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Filler,
)

const props = withDefaults(
  defineProps<{
    chartData: ChartData | null
    withWrapper?: boolean
    options?: ChartOptions
    type?: ChartType
    size?: 'big' | 'small' | 'thumbnail'
  }>(),
  {
    type: 'line',
  },
)

const { $lang } = useNuxtApp()

const options = computed(() => {
  return {
    responsive: true,
    locale: $lang,
    ...(props.options ?? {}),
  }
})

const canvas = useTemplateRef('canvas')
const chart = shallowRef<Chart | null>(null)

onMounted(async () => renderChart())

onBeforeUnmount(() => destroyChart())

watch(toRef(props, 'chartData'), async () => renderChart(), { deep: true })

async function renderChart() {
  destroyChart()
  await nextTick(() => {
    if (props.chartData && canvas.value) {
      chart.value = new Chart(canvas.value, {
        type: 'line',
        data: props.chartData,
        options: options.value,
      })
    }
  })
}

function destroyChart() {
  chart.value?.destroy()
}
</script>
